import { supabase } from "@/integrations/supabase/client";

/**
 * Helper to invoke Supabase Edge Functions with user's JWT token
 * This ensures RLS policies are respected
 * Falls back to userId from localStorage if no session is available (booking flow)
 */
export async function invokeFunctionWithAuth<T = any>(
  functionName: string,
  body?: any
): Promise<{ data: T; error: null } | { data: null; error: any }> {
  // Get current session to extract access token
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();

  // Prepare headers - include user's JWT if available
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  } else {
    // No session - try to get userId from localStorage (booking flow fallback)
    // Edge functions should handle userId in body when no JWT is provided
    const authSessionStr = localStorage.getItem('auth_session');
    if (authSessionStr) {
      try {
        const authSession = JSON.parse(authSessionStr);
        const userId = authSession.userId || authSession.user?.id;
        
        if (userId && body) {
          // Ensure userId is in body for edge functions that need it
          if (!body.userId && !body.user_id) {
            body.userId = userId;
          }
          // No JWT session - using userId from localStorage
        }
      } catch (e) {
        console.warn('Error parsing auth session:', e);
      }
    }
  }
  
  // Always include apikey for Supabase Edge Functions
  headers['apikey'] = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { message: errorText };
    }
    
    return {
      data: null,
      error: {
        message: errorData.error || errorData.message || `HTTP ${response.status}`,
        status: response.status,
      },
    };
  }

  const data = await response.json();
  return { data, error: null };
}

